import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { enumShort } from './chefShared'

const EVENT = 'choosechow-reservation-basket'
export const MAX_BASKET_ORDERS = 96
const keyFor = (profileId: string) => `choosechow-reservation-basket:${profileId}`

/** Accept older PostgreSQL timestamps without browser-specific date parsing. */
export const reservationScheduledAt = (value: unknown): string => String(value ?? '').trim()
  .replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T')
  .replace(/([+-]\d{2})$/, '$1:00')

export const reservationBasketIds = (profileId: string): string[] => {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(keyFor(profileId)) ?? '[]')
    return Array.isArray(value)
      ? [...new Set(value.filter((id): id is string => typeof id === 'string' && id.length > 0))].slice(0, MAX_BASKET_ORDERS)
      : []
  } catch {
    return []
  }
}

export const addReservationBasketIds = (profileId: string, ids: readonly string[]) => {
  if (!profileId) throw new Error('Sign in before adding planned meals to your cart.')
  const next = [...new Set([...reservationBasketIds(profileId), ...ids.filter(Boolean)])]
  if (next.length > MAX_BASKET_ORDERS) throw new Error('Your cart can hold up to 96 planned meals. Pay for this basket first.')
  localStorage.setItem(keyFor(profileId), JSON.stringify(next))
  window.dispatchEvent(new Event(EVENT))
}

export const removeReservationBasketIds = (profileId: string, ids: readonly string[]) => {
  const removed = new Set(ids)
  localStorage.setItem(keyFor(profileId), JSON.stringify(reservationBasketIds(profileId).filter((id) => !removed.has(id))))
  window.dispatchEvent(new Event(EVENT))
}

/** Clear every selection, including orders unavailable to the current detail read. */
export const clearReservationBasket = (profileId: string) => {
  localStorage.removeItem(keyFor(profileId))
  window.dispatchEvent(new Event(EVENT))
}

/** Planned meals must retain an unpaid balance and a future delivery time. */
export const isEligibleBasketOrder = (order: Record<string, unknown>, now = Date.now()): boolean =>
  enumShort(order.status, '') === 'pending_payment' &&
  Number(order.total_minor ?? 0) > Number(order.amount_paid_minor ?? 0) &&
  new Date(reservationScheduledAt(order.scheduled_at)).getTime() > now

const subscribe = (changed: () => void) => {
  window.addEventListener(EVENT, changed)
  window.addEventListener('storage', changed)
  return () => {
    window.removeEventListener(EVENT, changed)
    window.removeEventListener('storage', changed)
  }
}

export const useReservationBasketIds = (profileId: string): readonly string[] => {
  const raw = useSyncExternalStore(subscribe, () => {
    try { return localStorage.getItem(keyFor(profileId)) ?? '[]' } catch { return '[]' }
  }, () => '[]')
  return useMemo(() => {
    try {
      const value: unknown = JSON.parse(raw)
      return Array.isArray(value) ? [...new Set(value.filter((id): id is string => typeof id === 'string'))].slice(0, MAX_BASKET_ORDERS) : []
    } catch { return [] }
  }, [raw])
}

/** Read original orders before payment. Missing or failed details block checkout. */
export const useReservationBasketOrders = (profileId: string, records: readonly Record<string, unknown>[]) => {
  const ids = useReservationBasketIds(profileId)
  const live = isLiveRuntime()
  const [attempt, setAttempt] = useState(0)
  const signature = JSON.stringify([attempt, profileId, ids, records.filter((row) => ids.includes(String(row.id))).map((row) => [row.id, row.status, row.total_minor, row.amount_paid_minor])])
  const [loaded, setLoaded] = useState<{ signature: string; orders: Record<string, unknown>[]; failed: boolean }>({ signature: '', orders: [], failed: false })
  useEffect(() => {
    if (!live || ids.length === 0) return
    let active = true
    void (async () => {
      const orders: Record<string, unknown>[] = []
      let failed = false
      for (let offset = 0; offset < ids.length && active; offset += 4) {
        const results = await Promise.allSettled(ids.slice(offset, offset + 4).map((id) => commands.marketplace.getOrder(id)))
        for (const result of results) {
          if (result.status === 'rejected') { failed = true; continue }
          const detail = commandPayload(result.value)
          const order = detail.order as Record<string, unknown> | undefined
          if (!order || String(order.customer_profile_id ?? '') !== profileId) { failed = true; continue }
          const items = Array.isArray(detail.items) ? detail.items as Record<string, unknown>[] : []
          const scheduledAt = reservationScheduledAt(order.scheduled_at)
          if (!Number.isFinite(new Date(scheduledAt).getTime())) { failed = true; continue }
          orders.push({ ...order, scheduled_at: scheduledAt, amount_paid_minor: detail.amount_paid_minor ?? order.amount_paid_minor ?? 0, summary: items.map((item) => `${item.item_name}${Number(item.quantity) > 1 ? ` ×${item.quantity}` : ''}`).join(', ') })
        }
      }
      if (active) setLoaded({ signature, orders, failed })
    })()
    return () => { active = false }
    // The signature includes every field that changes payment eligibility.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, live])
  const orders = live ? loaded.signature === signature ? loaded.orders : [] : records.filter((row) => ids.includes(String(row.id)) && String(row.customer_profile_id) === profileId)
  const eligible = orders.filter(order => isEligibleBasketOrder(order))
  return { orders: eligible, unavailable: orders.length - eligible.length, retry: () => setAttempt((value) => value + 1), pending: live && ids.length > 0 && loaded.signature !== signature, failed: live && loaded.signature === signature && loaded.failed }
}
